import { assertNever } from '@kofno/piper';
import { action, makeObservable, observable } from 'mobx';
import { GroupResource } from '../../../../../GroupsStore/Types';
import { MulticheckStore } from '../../../../../Multicheck/Store';
import { fieldsToTemplate, fieldsToValues } from '../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../Resource/Types';
import { missingTemplate } from '../../../Participants/BulkActions/AllocateLicenseToParticipants/Types';
import {
  LoadError,
  Ready,
  ReadyResource,
  State,
  SubmitError,
  SubmittedResource,
  Submitting,
  loading,
  loadingError,
  ready,
  submitted,
  submitting,
  submittingError,
  waiting,
} from './Types';

export class AddLicenseToGroupsStore {
  @observable
  state: State = waiting();

  constructor() {
    makeObservable(this);
  }

  @action
  waiting = (): void => {
    this.state = waiting();
  };

  @action
  loading = (link: Link, multicheckStore: MulticheckStore<GroupResource>): void => {
    switch (this.state.kind) {
      case 'waiting':
        this.state = loading(link, multicheckStore);
        break;
      case 'loading':
      case 'loading-error':
      case 'ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadingError = (error: LoadError): void => {
    switch (this.state.kind) {
      case 'loading':
        this.state = loadingError(this.state.multicheckStore, error);
        break;
      case 'waiting':
      case 'loading-error':
      case 'ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  ready = (resource: ReadyResource): void => {
    switch (this.state.kind) {
      case 'loading':
        const template = resource.templates.self;
        this.state = template
          ? ready(resource, template, this.state.multicheckStore)
          : loadingError(this.state.multicheckStore, missingTemplate('self'));
        break;
      case 'waiting':
      case 'loading-error':
      case 'ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitting = (): void => {
    switch (this.state.kind) {
      case 'ready':
        const { resource, template, multicheckStore } = this.state;
        this.state = fieldsToValues(template.fields)
          .mapError(fieldsToTemplate(template))
          .cata<Submitting | Ready>({
            Ok: (values) => submitting(resource, template, values, multicheckStore),
            Err: (errTemplate) => ready(resource, errTemplate, multicheckStore),
          });
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submittingError = (error: SubmitError): void => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submittingError(this.state.resource, this.state.template, error);
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'ready':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitted = (resource: SubmittedResource): void => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submitted(resource, this.state.template, this.state.multicheckStore);
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'ready':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };
}
